import React from 'react'
import { useTranslation } from 'react-i18next'

import type { EmpDocumentListItem, EmpQualificationListItem, Employee, SkillListItem } from '@/api/types'
import { formatDate } from '@/lib/format'
import {
  employeeFullName,
  employeeInitials,
  isBlank,
  resolveMediaUrl,
} from '@/modules/employees/employeeDisplay'

interface EmployeeCvLayoutProps {
  employee: Employee
  qualifications: EmpQualificationListItem[]
  skills: SkillListItem[]
  documents: EmpDocumentListItem[]
  maritalName?: string
  linkedin?: string
}

function text(value?: string | number | null): string {
  if (isBlank(value)) return ''
  return String(value).trim()
}

function yearRange(start?: number | string | null, end?: number | string | null): string {
  const from = text(start)
  const to = text(end)
  if (from && to) return `${from} – ${to}`
  return from || to
}

export const EmployeeCvLayout: React.FC<EmployeeCvLayoutProps> = ({
  employee,
  qualifications,
  skills,
  documents,
  maritalName,
  linkedin,
}) => {
  const { t } = useTranslation('common')
  const name = employeeFullName(employee)
  const photo = resolveMediaUrl(employee.imageUrl || employee.profileImageUrl)
  const title = text(employee.jobName) || t('employees.cv.employee')
  const department = text(employee.departmentName)
  const location = text(employee.empPostedLocation) || text(employee.empAddress)
  const email = text(employee.empEmail)
  const phone = text(employee.empMobileNo) || text(employee.companyMobileNo)
  const linkedinUrl = text(linkedin)
  const joining = employee.joiningDate ? formatDate(employee.joiningDate, { format: 'long' }) : ''
  const dob = employee.empDob ? formatDate(employee.empDob, { format: 'long' }) : ''
  const nationality = text(employee.nationalityName)
  const gender = text(employee.genderName)
  const marital = text(maritalName)
  const father = text(employee.empFatherNameEn) || text(employee.empFatherNameAr)
  const birthPlace = text(employee.empBirthPlace)
  const iqama = text(employee.iqamaNo)
  const passport = text(employee.passportNo)
  const initials = employeeInitials(employee)

  const skillItems = skills
    .map((s) => ({
      name: text(s.skillTypeName),
      years: s.experienceYear ?? s.empExperienceYear,
    }))
    .filter((s) => s.name)

  const certs = documents
    .map((d) => text(d.documentName || d.documentTypeName))
    .filter(Boolean)

  const hasExperience = Boolean(title || department || joining || location)
  const hasPersonal = Boolean(nationality || dob || gender || marital || father || birthPlace)
  const hasId = Boolean(iqama || passport)

  return (
    <div className="emp-cv-sheet">
      <style>{`
        .emp-cv-sheet {
          display: grid;
          grid-template-columns: 68mm 1fr;
          width: 100%;
          background: #fff;
          color: #1e293b;
          font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
          font-size: 11.5px;
          line-height: 1.45;
        }
        .emp-cv-side {
          background: #163a5f;
          color: #fff;
          padding: 22px 16px 28px;
        }
        .emp-cv-main {
          padding: 22px 22px 28px 24px;
          background: #fff;
        }
        .emp-cv-photo {
          width: 92px;
          height: 92px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid rgba(255,255,255,0.35);
          display: block;
          margin: 0 auto 14px;
          background: #1f4d7a;
        }
        .emp-cv-initials {
          width: 92px;
          height: 92px;
          border-radius: 50%;
          margin: 0 auto 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #1f4d7a;
          border: 3px solid rgba(255,255,255,0.35);
          font-size: 28px;
          font-weight: 700;
          letter-spacing: 1px;
        }
        .emp-cv-side-title {
          font-size: 10px;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          font-weight: 700;
          color: #9ec5e8;
          border-bottom: 1px solid rgba(255,255,255,0.22);
          padding-bottom: 5px;
          margin: 18px 0 10px;
        }
        .emp-cv-contact-item {
          font-size: 11px;
          margin: 0 0 8px;
          word-break: break-word;
          color: #e8f1f8;
        }
        .emp-cv-kv {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2px;
          margin-bottom: 8px;
        }
        .emp-cv-k {
          font-size: 9px;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: #9ec5e8;
        }
        .emp-cv-v { font-size: 12px; color: #fff; font-weight: 600; }
        .emp-cv-skill {
          display: inline-block;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.18);
          color: #fff;
          border-radius: 999px;
          padding: 3px 9px;
          margin: 0 6px 6px 0;
          font-size: 10.5px;
        }
        .emp-cv-name {
          font-size: 28px;
          line-height: 1.15;
          font-weight: 800;
          color: #0f2744;
          letter-spacing: 0.2px;
          margin: 0 0 4px;
        }
        .emp-cv-role {
          font-size: 14px;
          color: #1d4f7a;
          font-weight: 700;
          margin: 0 0 2px;
        }
        .emp-cv-meta {
          font-size: 11.5px;
          color: #64748b;
          margin: 0 0 10px;
        }
        .emp-cv-rule {
          height: 3px;
          width: 56px;
          background: #c9a227;
          margin: 0 0 16px;
        }
        .emp-cv-h {
          font-size: 12px;
          letter-spacing: 1.6px;
          text-transform: uppercase;
          font-weight: 800;
          color: #163a5f;
          border-bottom: 1.5px solid #dbe4ee;
          padding-bottom: 5px;
          margin: 0 0 12px;
        }
        .emp-cv-block { margin-bottom: 18px; page-break-inside: avoid; break-inside: avoid; }
        .emp-cv-job { margin-bottom: 12px; }
        .emp-cv-job-top {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 8px;
          align-items: baseline;
        }
        .emp-cv-job-title { font-size: 14px; font-weight: 800; color: #0f2744; margin: 0; }
        .emp-cv-job-dates { font-size: 11px; color: #1d4f7a; font-weight: 700; white-space: nowrap; }
        .emp-cv-job-org { font-size: 12.5px; color: #334155; margin: 2px 0 0; font-weight: 600; }
        .emp-cv-job-sub { font-size: 11.5px; color: #64748b; margin: 2px 0 0; }
        .emp-cv-edu-desc { font-size: 11.5px; color: #475569; margin: 4px 0 0; }
        .emp-cv-certs { margin: 0; padding-left: 16px; color: #334155; }
        .emp-cv-certs li { margin-bottom: 3px; }
        @media print {
          html.emp-print-cv .emp-cv-sheet,
          html.emp-print-cv .emp-cv-sheet * {
            max-width: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html.emp-print-cv .emp-cv-sheet .flex { flex-wrap: nowrap !important; }
          .emp-cv-sheet {
            width: 210mm;
            height: 297mm;
            min-height: 297mm;
            max-height: 297mm;
            overflow: hidden;
          }
          .emp-cv-side { background: #163a5f !important; color: #fff !important; height: 297mm; }
        }
      `}</style>

      <div className="emp-cv-side">
        {photo ? (
          <img className="emp-cv-photo" src={photo} alt={name} />
        ) : (
          <div className="emp-cv-initials">{initials}</div>
        )}

        {(email || phone || location || linkedinUrl) ? (
          <>
            <div className="emp-cv-side-title">{t('employees.cv.contact')}</div>
            {email ? <p className="emp-cv-contact-item">{email}</p> : null}
            {phone ? <p className="emp-cv-contact-item">{phone}</p> : null}
            {location ? <p className="emp-cv-contact-item">{location}</p> : null}
            {linkedinUrl ? <p className="emp-cv-contact-item">{linkedinUrl}</p> : null}
          </>
        ) : null}

        {hasPersonal ? (
          <>
            <div className="emp-cv-side-title">{t('employees.cv.personal')}</div>
            {nationality ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.nationality')}</span>
                <span className="emp-cv-v">{nationality}</span>
              </div>
            ) : null}
            {dob ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.dateOfBirth')}</span>
                <span className="emp-cv-v">{dob}</span>
              </div>
            ) : null}
            {gender ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.gender')}</span>
                <span className="emp-cv-v">{gender}</span>
              </div>
            ) : null}
            {marital ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.maritalStatus')}</span>
                <span className="emp-cv-v">{marital}</span>
              </div>
            ) : null}
            {father ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.fatherName')}</span>
                <span className="emp-cv-v">{father}</span>
              </div>
            ) : null}
            {birthPlace ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.birthPlace')}</span>
                <span className="emp-cv-v">{birthPlace}</span>
              </div>
            ) : null}
          </>
        ) : null}

        {skillItems.length > 0 ? (
          <>
            <div className="emp-cv-side-title">{t('employees.cv.skills')}</div>
            <div>
              {skillItems.map((s) => (
                <span key={s.name} className="emp-cv-skill">
                  {s.name}{s.years ? ` · ${s.years}y` : ''}
                </span>
              ))}
            </div>
          </>
        ) : null}

        {hasId ? (
          <>
            <div className="emp-cv-side-title">{t('employees.cv.identification')}</div>
            {iqama ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.iqamaNo')}</span>
                <span className="emp-cv-v">{iqama}</span>
              </div>
            ) : null}
            {passport ? (
              <div className="emp-cv-kv">
                <span className="emp-cv-k">{t('employees.profile.passportNo')}</span>
                <span className="emp-cv-v">{passport}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="emp-cv-main">
        <h1 className="emp-cv-name">{name}</h1>
        <p className="emp-cv-role">{title}</p>
        <p className="emp-cv-meta">
          {[department, text(employee.empCode)].filter(Boolean).join('  ·  ')}
        </p>
        <div className="emp-cv-rule" />

        {hasExperience ? (
          <section className="emp-cv-block">
            <h2 className="emp-cv-h">{t('employees.cv.experience')}</h2>
            <div className="emp-cv-job">
              <div className="emp-cv-job-top">
                <p className="emp-cv-job-title">{title}</p>
                <span className="emp-cv-job-dates">
                  {joining ? `${joining} – ${t('employees.cv.present')}` : t('employees.cv.present')}
                </span>
              </div>
              {department ? <p className="emp-cv-job-org">{department}</p> : null}
              {location ? <p className="emp-cv-job-sub">{location}</p> : null}
            </div>
          </section>
        ) : null}

        {qualifications.length > 0 ? (
          <section className="emp-cv-block">
            <h2 className="emp-cv-h">{t('employees.cv.education')}</h2>
            {qualifications.map((q, index) => {
              const degree = text(q.empDegreeName)
              const institute = text(q.empInstituteName)
              const years = yearRange(q.empStartYear, q.empEndYear)
              const desc = text(q.empDescription)
              if (!degree && !institute && !years) return null
              return (
                <div key={q.id || index} className="emp-cv-job">
                  <div className="emp-cv-job-top">
                    <p className="emp-cv-job-title">{degree || institute || t('employees.cv.education')}</p>
                    {years ? <span className="emp-cv-job-dates">{years}</span> : null}
                  </div>
                  {institute && institute !== degree ? <p className="emp-cv-job-org">{institute}</p> : null}
                  {desc ? <p className="emp-cv-edu-desc">{desc}</p> : null}
                </div>
              )
            })}
          </section>
        ) : null}

        {certs.length > 0 ? (
          <section className="emp-cv-block">
            <h2 className="emp-cv-h">{t('employees.cv.certificates')}</h2>
            <ul className="emp-cv-certs">
              {certs.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  )
}
